import type {
  AssessmentBankFolder,
  AssessmentBankQuestion,
} from "@/lib/mock/assessment-add-questions"
import type {
  LibraryBloomLevel,
  LibraryItem,
  LibraryItemType,
  LibraryLevel,
} from "@/lib/mock/library"
import type {
  LibraryFolder,
  LibraryFolderColorKey,
} from "@/lib/mock/library-folders"
import { libraryAiSpotted } from "@/lib/library-ai-spotted"

const FOLDER_COLOR_CYCLE: LibraryFolderColorKey[] = [
  "brand",
  "chart1",
  "chart2",
  "chart3",
  "success",
  "warning",
  "muted",
]

const TYPE_TO_LIBRARY: Record<string, LibraryItemType> = {
  "Multiple Choice": "multiple_choice",
  "Multiple Select": "multiple_select",
  "True or False": "true_false",
  "Short Answer": "short_answer",
  Essay: "essay",
  "Fill in the blank": "fill_blank",
  "Match the following": "matching",
  Hotspot: "hotspot",
}

function mapLibraryType(type: string): LibraryItemType {
  return TYPE_TO_LIBRARY[type] ?? "multiple_choice"
}

function mapLibraryDifficulty(difficulty: string): LibraryLevel {
  const normalized = difficulty.trim().toLowerCase()
  if (normalized === "easy") return "easy"
  if (normalized === "hard") return "hard"
  return "medium"
}

function parsePbi(value: string | number | undefined): number | undefined {
  if (value == null || value === "--") return undefined
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value))
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Map the large assessment add-questions bank into Question Bank catalog rows. */
export function assessmentBankQuestionToLibraryItem(
  row: AssessmentBankQuestion,
  index: number,
): LibraryItem {
  const pbi = parsePbi(row.pbi)
  const difficulty = mapLibraryDifficulty(String(row.difficulty ?? "Medium"))

  return {
    id: row.id,
    questionId: `LIB-2026-${String(index + 1).padStart(4, "0")}`,
    title: row.title,
    stem: row.stem,
    topic: row.topic,
    categoryIds: row.categoryIds,
    status: row.status === "Draft" ? "Draft" : "Saved",
    type: mapLibraryType(String(row.type)),
    difficulty,
    author: row.author ?? "Unknown",
    authorEmail: row.authorEmail,
    updatedAt: "2026-03-01",
    folderId: row.folderId,
    bloomLevel: row.bloom as LibraryBloomLevel | undefined,
    pbi,
    ptBiserial: pbi,
    difficultyPercent:
      difficulty === "easy" ? 72 : difficulty === "hard" ? 38 : 55,
    usageCount: row.timesUsed ?? 0,
    examUsageCount: row.timesUsed ?? 0,
    importedAt: row.importedAt,
    isNew: row.isNew,
    tags: row.tags,
    aiSpotted: libraryAiSpotted({
      needsAttention: row.importSignals?.needsAttention,
      pbi: row.pbi == null || row.pbi === "--" ? null : pbi,
    }) ?? undefined,
  }
}

/** Subject tree from Add questions — same folder ids as `ASSESSMENT_BANK_QUESTIONS`. */
export function assessmentBankFoldersToLibraryFolders(
  folders: AssessmentBankFolder[],
): LibraryFolder[] {
  return folders.map((folder, index) => ({
    id: folder.id,
    name: folder.name,
    parentId: folder.parentId,
    icon: folder.parentId === null ? "fa-book" : "fa-folder",
    colorKey: FOLDER_COLOR_CYCLE[index % FOLDER_COLOR_CYCLE.length]!,
  }))
}
